import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { AppState } from './app.service';
import { NavigationEnd, Router } from "@angular/router";
import { LoginSharedService } from './login/login-shared.service';
import { LoginService } from './login/login.service';
import { NotificationService } from './shared/header/notification.service';
import { Subscription } from 'rxjs';
import { SessionService } from './session-maintenance/session.service';
import { HttpHeaders } from '@angular/common/http';
import { LocalizationService } from './localization.service';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'my-app',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./app.component.css'],
    templateUrl: 'app.component.html',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {

    public agreementModal: boolean = false;
    public showProgressAccept: boolean = false;
    public disabledContinue: boolean = false;
    public sessionEvents: any = {
        clearCookies: false,
        showSessionRefreshConfirmation: false,
        showSessionRefreshConfirmationProgress: false,
        showSessionResetNotification: false,
        showWindowInactiveNotification: false
    };

    private subscription: Subscription = new Subscription();
    private somethingWentWrongTranslation: string = 'Something went wrong!';
    private readonly translationSub: Subscription = new Subscription();
    private routerSyncSubscription: Subscription = new Subscription();

    constructor(public appState: AppState,
        public router: Router,
        private readonly loginShared: LoginSharedService,
        private readonly loginService: LoginService,
        private readonly notificationService: NotificationService,
        private readonly sessionService: SessionService,
        private readonly localizationService: LocalizationService,
        private readonly translate: TranslateService
    ) {
        this.loadSession = this.loadSession.bind(this);
    }

    ngOnInit() {
        // Register router sync for language changes after bootstrap
        this.registerRouterSync();
        this.subscription = this.appState.registerForSessionChange(res => {
            if (res) {
                this.appState.processResult(res.json());
                if (this.appState.hasUserConsent === false) {
                    this.agreementModal = true;
                }
                else this.loadSession();
            }
        });
        this.subscription.add(
            this.loginShared.registerSessionEvent().subscribe({
                next: (event) => {
                    switch (event) {
                        case 'Clear Cookies':
                            this.sessionEvents.clearCookies = true;
                            break;
                    }
                },
                error: () => { }
            })
        );
        this.subscription.add(
            this.sessionService.registerForSessionUpdates()
            .pipe(filter(event => (this.loginShared.isLoggedIn() && event.type !== 'auto-session-update')
                || event.type === 'notify-window-inactive' || event.type === 'clear-session-update'
                || event.type === 'notify-session-reset'))
            .subscribe({
                next: (event) => {
                    this.sessionEvents.showSessionRefreshConfirmationProgress = false;
                    this.sessionEvents.showSessionRefreshConfirmation = event.type === 'confirm-session-update';
                    this.sessionEvents.showSessionResetNotification = event.type === 'notify-session-reset'
                        || (event.type === 'clear-session-update' && this.sessionEvents.showSessionResetNotification);
                    this.sessionEvents.showWindowInactiveNotification = event.type === 'notify-window-inactive';
                },
                error: () => { }
            })
        );
        this.translationSub.add(
            this.translate.stream('Something went wrong!').subscribe({
                next: (translated) => { this.somethingWentWrongTranslation = translated; },
                error: () => { }
            })
        );
    }
  
    public ngOnDestroy() {
        this.routerSyncSubscription.unsubscribe();
        this.subscription.unsubscribe();
        this.translationSub.unsubscribe();
    }
  
    public acceptConsent() {
        this.showProgressAccept = true;
        this.disabledContinue = true;
        this.loginService.provideUserConsent().then(res => {
            this.appState.processResult(res);
            this.loadSession();
        }).catch(err => {
            this.showProgressAccept = false;
            this.disabledContinue = false;
            this.notificationService.notify({
                type: 'info',
                title: this.somethingWentWrongTranslation,
                config: { timeOut: 2500 }
            });
        });
    }

    private loadSession() {
        this.showProgressAccept = false;
        this.disabledContinue = false;
        if (this.appState.hasUserConsent === false) {
            this.notificationService.notify({
                type: 'info',
                title: this.somethingWentWrongTranslation,
                config: { timeOut: 2500 }
             });
            return;
        }
        this.agreementModal = false;
    }

    public refreshSession(refreshCall: boolean = false) {
        if (refreshCall) {
            this.sessionEvents.showSessionRefreshConfirmationProgress = true;
            this.appState.refreshToken(new HttpHeaders({ 'Expire-Cookies': 'None' })).subscribe({
                next: () => {
                    this.sessionService.updateSessionLastAccessedTime();
                    this.sessionEvents.showSessionRefreshConfirmationProgress = false;
                },
                error: () => { }
            });
        }
    }

    /**
     * Register for router navigation events to sync language from URL.
     * Called after bootstrap.
     * @param router - Router instance passed from app component to avoid circular dependency
     */
    private registerRouterSync(): void {
        this.routerSyncSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe({
            next: (nav: NavigationEnd) => {
                const lang = this.localizationService.extractLanguageFromUrl();
                if (lang && lang !== this.translate.currentLang) {
                    this.localizationService.setLanguage(lang);
                }
            },
            error: () => { }
        });
    }
}
